import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { SummaryCard } from "../components/home/SummaryCard";
import { useControlCash } from "../store/controlCashStore";
import { useConfirm } from "../contexts/ConfirmContext";
import { useToast } from "../contexts/ToastContext";

// ============================================================================
// COMPONENTE: PageHeader
// ============================================================================
const PageHeader = ({ onBack }) => (
  <div className="mb-8">
    <button
      onClick={onBack}
      className="flex items-center gap-2 text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium mb-4 transition-colors group"
      aria-label="Volver al inicio"
    >
      <svg className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
      </svg>
      Volver
    </button>
    <h1 className="text-2xl md:text-4xl font-bold text-gray-900 dark:text-white mb-2 tracking-tight">
      Ingresos recurrentes
    </h1>
    <p className="text-base md:text-lg text-gray-600 dark:text-gray-400">
      Crea ingresos que se repiten cada mes. Se agregarán automáticamente al crear un nuevo mes.
    </p>
  </div>
);

// ============================================================================
// COMPONENTE: SummaryStats (diseño unificado con Home)
// ============================================================================
const SummaryStats = ({ templates, formatAmount, currency }) => {
  const activeTemplates = templates.filter(t => t.active);
  const totalMonthly = activeTemplates.reduce((sum, t) => sum + t.amount, 0);

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-10">
      <SummaryCard title="Activos" amount={String(activeTemplates.length)} currency={currency} color="green" />
      <SummaryCard title="Total mensual estimado" amount={formatAmount(totalMonthly)} currency={currency} color="blue" />
    </div>
  );
};

// ============================================================================
// COMPONENTE: IncomeTemplateCard
// ============================================================================
const IncomeTemplateCard = ({ template, onEdit, onDelete, onToggleActive, formatAmount }) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 768px)");
    const handler = () => setIsMobileView(!mq.matches);
    handler();
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);

  const showCollapsed = !isExpanded && isMobileView;
  const showExpanded = isExpanded || !isMobileView;

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 hover:shadow-lg transition-all overflow-hidden ${!template.active ? "opacity-60" : ""}`}>
      <button
        type="button"
        onClick={() => isMobileView && setIsExpanded(!isExpanded)}
        className="w-full bg-gradient-to-r from-blue-600 to-purple-600 px-5 py-3 flex items-center justify-between cursor-pointer hover:opacity-95 transition-opacity text-left"
        aria-expanded={showExpanded}
      >
        <span className="text-sm font-semibold text-white">{template.name}</span>
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 bg-white/20 text-white text-xs font-medium rounded-full">
            {template.active ? "Activo" : "Inactivo"}
          </span>
          <svg className={`w-5 h-5 text-white transition-transform md:hidden ${showExpanded ? "rotate-180" : ""}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {showCollapsed && (
        <button
          type="button"
          className="w-full px-5 py-3 flex items-center justify-between border-b border-gray-100 dark:border-gray-700 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors text-left"
          onClick={() => setIsExpanded(true)}
          aria-label="Expandir"
        >
          <span className="text-base font-bold text-gray-900 dark:text-white">{formatAmount(template.amount)}</span>
        </button>
      )}

      {showExpanded && (
        <div className="p-5">
          <div className="flex items-center justify-between mb-4">
            <span className="text-lg font-bold text-gray-900 dark:text-white">{formatAmount(template.amount)}</span>
          </div>
          <div className="flex items-center justify-between pt-4 border-t border-gray-100">
            <button
              onClick={() => onToggleActive(template.id)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${template.active ? "bg-green-500" : "bg-gray-300"}`}
              aria-label={template.active ? "Desactivar" : "Activar"}
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow-lg transition-transform ${template.active ? "translate-x-6" : "translate-x-1"}`} />
            </button>
            <div className="flex gap-2">
              <button onClick={() => onEdit(template)} className="px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 text-sm font-medium rounded-lg flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
                Editar
              </button>
              <button onClick={() => onDelete(template.id, template.name)} className="px-3 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-sm font-medium rounded-lg flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// COMPONENTE: IncomeTemplateModal
// ============================================================================
const IncomeTemplateModal = ({ isOpen, onClose, onSave, editingTemplate, currency, toDisplayAmount }) => {
  const [formData, setFormData] = useState({ name: "", amount: "", active: true });
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (editingTemplate) {
      setFormData({
        name: editingTemplate.name,
        amount: toDisplayAmount ? toDisplayAmount(editingTemplate.amount).toString() : String(editingTemplate.amount),
        active: editingTemplate.active
      });
    } else {
      setFormData({ name: "", amount: "", active: true });
    }
    setErrors({});
  }, [editingTemplate, isOpen, toDisplayAmount]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const newErrors = {};
    if (!formData.name.trim()) newErrors.name = "Nombre requerido";
    const amount = parseFloat(formData.amount);
    if (isNaN(amount) || amount < 0) newErrors.amount = "Monto inválido";
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    onSave({
      name: formData.name.trim(),
      amount: amount,
      active: formData.active
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
          <h2 className="text-xl font-bold text-gray-800 mb-6">
            {editingTemplate ? "Editar ingreso" : "Nuevo ingreso recurrente"}
          </h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Nombre</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                placeholder="Ej: Sueldo, Freelance, Alquiler..."
                className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 ${errors.name ? "border-red-300" : "border-gray-200 focus:border-green-500"}`}
              />
              {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
            </div>
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-1">Monto mensual</label>
              <div className="relative">
                <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">{currency}</span>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData(p => ({ ...p, amount: e.target.value }))}
                  placeholder="0.00"
                  className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 ${errors.amount ? "border-red-300" : "border-gray-200 focus:border-green-500"}`}
                />
              </div>
              {errors.amount && <p className="mt-1 text-sm text-red-600">{errors.amount}</p>}
            </div>
            {editingTemplate && (
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setFormData(p => ({ ...p, active: !p.active }))}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full ${formData.active ? "bg-green-500" : "bg-gray-300"}`}
                >
                  <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${formData.active ? "translate-x-6" : "translate-x-1"}`} />
                </button>
                <span className="text-sm font-medium text-gray-700">Activo</span>
              </div>
            )}
            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="flex-1 px-4 py-2 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50">
                Cancelar
              </button>
              <button type="submit" className="flex-1 px-4 py-2 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl">
                {editingTemplate ? "Guardar" : "Crear"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// PÁGINA PRINCIPAL
// ============================================================================
const RecurringIncomes = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { state, dispatch, formatDisplayAmount, toStorageAmount, toDisplayAmount } = useControlCash();
  const { confirm } = useConfirm();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);

  const templates = Array.isArray(state.recurringIncomeTemplates) ? state.recurringIncomeTemplates : [];

  const handleSave = (data) => {
    const amountToStore = toStorageAmount(data.amount);
    if (editingTemplate) {
      dispatch({
        type: "UPDATE_RECURRING_INCOME_TEMPLATE",
        payload: { id: editingTemplate.id, patch: { name: data.name, amount: amountToStore, active: data.active } }
      });
    } else {
      dispatch({
        type: "CREATE_RECURRING_INCOME_TEMPLATE",
        payload: { name: data.name, amount: amountToStore, active: data.active }
      });
    }
    setEditingTemplate(null);
    setIsModalOpen(false);
  };

  const handleDelete = async (id, name) => {
    const ok = await confirm({
      title: "Eliminar ingreso recurrente",
      message: `¿Eliminar "${name}"?`,
      confirmLabel: "Eliminar"
    });
    if (ok) {
      try {
        await dispatch({ type: "DELETE_RECURRING_INCOME_TEMPLATE", payload: id });
      } catch (err) {
        console.error(err);
        toast.error(err?.message || "Error al eliminar. Intenta de nuevo.");
      }
    }
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        <PageHeader onBack={() => navigate("/")} />
        <SummaryStats templates={templates} formatAmount={formatDisplayAmount} currency={state.currencySymbol ?? state.currency} />

        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Mis ingresos recurrentes</h2>
          <button
            onClick={() => { setEditingTemplate(null); setIsModalOpen(true); }}
            className="px-5 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-2xl shadow-lg shadow-blue-500/25 hover:shadow-xl hover:shadow-blue-500/30 transition-all flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nuevo ingreso
          </button>
        </div>

        <div className="space-y-4">
          {templates.length === 0 ? (
            <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-200/50 dark:border-gray-700/50 p-12 text-center">
              <svg className="w-16 h-16 mx-auto mb-4 text-gray-300 dark:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <p className="text-gray-600 dark:text-gray-400 mb-6">No hay ingresos recurrentes. Crea plantillas como Sueldo o Freelance.</p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-semibold rounded-2xl shadow-lg shadow-blue-500/25 transition-all"
              >
                Crear primer ingreso recurrente
              </button>
            </div>
          ) : (
            templates.map(t => (
              <IncomeTemplateCard
                key={t.id}
                template={t}
                onEdit={(tpl) => { setEditingTemplate(tpl); setIsModalOpen(true); }}
                onDelete={handleDelete}
                onToggleActive={(id) => dispatch({ type: "TOGGLE_RECURRING_INCOME_TEMPLATE", payload: id })}
                formatAmount={formatDisplayAmount}
              />
            ))
          )}
        </div>

        <IncomeTemplateModal
          isOpen={isModalOpen}
          onClose={() => { setIsModalOpen(false); setEditingTemplate(null); }}
          onSave={handleSave}
          editingTemplate={editingTemplate}
          currency={state.currencySymbol ?? state.currency}
          toDisplayAmount={toDisplayAmount}
        />
      </div>
    </Layout>
  );
};

export default RecurringIncomes;
